/**
 * 赛事接口。契约（apps/api/src/routes/events.route.ts、tasks.route.ts）：
 *   GET  /events/:code            → {event}                  （公开，用于校验赛事码）
 *   GET  /events                  → {events}                 （登录；赛事列表页）
 *   POST /events                  → 201 {event}              （仅 organizer/admin）
 *   PATCH /events/:code           → {event}                  （organizer/admin：改名/状态）
 *   POST /events/:code/milestones → 201 {milestone}          （organizer/admin）
 *   GET  /events/:code/timeline   → {event, milestones, feeds}（登录；含 daysLeft 拉取式提醒）
 *   POST /events/:code/join       → {event, joined:true}     （登录；幂等）
 *   POST /projects/:id/prep-timeline → 201 {projectId, generated, skipped}（队伍成员；T85）
 */
import { api } from './client.js';
import type {
  EventCreatePayload,
  EventEnvelopeDto,
  EventScreenDto,
  EventStatsDto,
  EventTimelineDto,
  EventUpdatePayload,
  EventsListDto,
  MilestonePayload,
  PrepTimelineResultDto,
  RequirementSetDto,
} from './types.js';

/** 把赛事过滤条件拼成查询串（无过滤返回空串）。 */
function buildEventParams(filter?: { scope?: 'common' | 'exclusive'; school?: string }): string {
  if (!filter) return '';
  const parts: string[] = [];
  if (filter.scope === 'common' || filter.scope === 'exclusive') parts.push(`scope=${filter.scope}`);
  if (typeof filter.school === 'string' && filter.school.trim() !== '') parts.push(`school=${encodeURIComponent(filter.school.trim())}`);
  return parts.join('&');
}

export const eventsApi = {
  get: (code: string): Promise<EventEnvelopeDto> => api.get<EventEnvelopeDto>(`/events/${encodeURIComponent(code)}`),

  /** 赛事列表（登录可见）；可传 {scope, school} 过滤（v0.3 竞赛资讯分类） */
  list: (filter?: { scope?: 'common' | 'exclusive'; school?: string }): Promise<EventsListDto> => {
    const params = buildEventParams(filter);
    return api.get<EventsListDto>(params ? `/events?${params}` : '/events');
  },

  /** 赛事列表（公开：免登录可浏览，用于赛事主页招生）；可传 {scope, school} 过滤 */
  listPublic: (filter?: { scope?: 'common' | 'exclusive'; school?: string }): Promise<EventsListDto> => {
    const params = buildEventParams(filter);
    return api.get<EventsListDto>(params ? `/events?${params}` : '/events', false);
  },

  /** 时间轴（公开）：里程碑倒计时 + 动态，免登录可看。 */
  timelinePublic: (code: string): Promise<EventTimelineDto> =>
    api.get<EventTimelineDto>(`/events/${encodeURIComponent(code)}/timeline`, false),

  /** 赛事技能需求（公开：招募卡的公开依据）。 */
  requirementsPublic: (code: string): Promise<RequirementSetDto> =>
    api.get<RequirementSetDto>(`/events/${encodeURIComponent(code)}/requirements`, false),

  /**
   * 创建赛事（需 organizer/admin，否则后端 403 FORBIDDEN）。
   * 赛事码重复时后端返回 409 CONFLICT（`赛事码 X 已存在`）。
   */
  create: (input: EventCreatePayload): Promise<EventEnvelopeDto> => api.post<EventEnvelopeDto>('/events', input),

  /** 更新赛事名称/状态（organizer/admin） */
  update: (code: string, patch: EventUpdatePayload): Promise<EventEnvelopeDto> =>
    api.patch<EventEnvelopeDto>(`/events/${encodeURIComponent(code)}`, patch),

  /** 新增里程碑（organizer/admin） */
  addMilestone: (code: string, payload: MilestonePayload): Promise<EventEnvelopeDto> =>
    api.post<EventEnvelopeDto>(`/events/${encodeURIComponent(code)}/milestones`, payload),

  /** 时间轴：里程碑（含 daysLeft）+ 动态（登录） */
  timeline: (code: string): Promise<EventTimelineDto> =>
    api.get<EventTimelineDto>(`/events/${encodeURIComponent(code)}/timeline`),

  /** 加入赛事（幂等，重复加入不报错） */
  join: (code: string): Promise<EventEnvelopeDto & { joined: boolean }> =>
    api.post<EventEnvelopeDto & { joined: boolean }>(`/events/${encodeURIComponent(code)}/join`),

  /** 生成备赛规划（T85）：以赛事里程碑为锚倒推备赛任务落入项目看板 */
  prepTimeline: (projectId: string): Promise<PrepTimelineResultDto> =>
    api.post<PrepTimelineResultDto>(`/projects/${encodeURIComponent(projectId)}/prep-timeline`),

  /** 导出赛事日历 .ics（扩展④）：返回路径，下载走带 Bearer 的 fetch → Blob 另存 */
  calendarIcsPath: (code: string): string => `/events/${encodeURIComponent(code)}/calendar.ics`,

  /** 组织方聚合看板（扩展⑤）：仅 organizer/admin；n<5 的桶一律 "<5"（k-匿名） */
  stats: (code: string): Promise<EventStatsDto> =>
    api.get<EventStatsDto>(`/events/${encodeURIComponent(code)}/stats`),

  /** 组织方数据大屏（可视化②）：仅 organizer/admin；flows 中 count=null 为 k-匿名隐藏 */
  screen: (code: string): Promise<EventScreenDto> =>
    api.get<EventScreenDto>(`/events/${encodeURIComponent(code)}/screen`),
};
